import { NBSP } from 'shared/constants/text';
import type { IBuildingCard, IBuildingOffersAreas, IBuildingRates } from 'shared/types/buildingCard';
import { isTechVisible } from 'shared/utils/buildingDetails';
import { formatDecimal, formatNumber, noBreak } from 'shared/utils/format';

export interface IFloatingHeaderNavItem {
  /** id цели на странице, без `#` */
  targetId: 'about' | 'all-offers' | 'tech' | 'map';
  label: string;
  counter?: number;
}

/** Пункты навигации — как `floating_header.html` монолита. «На карте» — по координатам, как и блок карты */
export function getFloatingHeaderNavItems(card: IBuildingCard): IFloatingHeaderNavItem[] {
  const items: IFloatingHeaderNavItem[] = [{ targetId: 'about', label: 'Об объекте' }];

  if (card.offerCategories.length) {
    // В монолите при пустом `propsCount` выводилось «None» — счётчика нет
    items.push({ targetId: 'all-offers', label: 'Предложения', counter: card.propsCount ?? undefined });
  }
  if (isTechVisible(card)) {
    items.push({ targetId: 'tech', label: 'Технические характеристики' });
  }
  if (card.coordinates) {
    items.push({ targetId: 'map', label: 'На карте' });
  }

  return items;
}

/**
 * Площади помещений: `2 – 6 400 м²` или `6 400 м²` (`offers_min_area` / `offers_max_area` презентера монолита —
 * min/max непустых площадей аренды и продажи с округлением вверх)
 */
export function getFloatingHeaderArea({ rentMin, rentMax, saleMin, saleMax }: IBuildingOffersAreas): string | null {
  const areas = [rentMax, saleMax, rentMin, saleMin].filter((area): area is number => Boolean(area));

  if (!areas.length) {
    return null;
  }

  const min = Math.ceil(Math.min(...areas));
  const max = Math.ceil(Math.max(...areas));

  if (min === max) {
    return `${noBreak(formatNumber(min))}${NBSP}м²`;
  }

  // Перенос возможен только после тире: `<nobr>{min} –</nobr> <nobr>{max} м²</nobr>`
  return `${noBreak(formatNumber(min))}${NBSP}– ${noBreak(formatNumber(max))}${NBSP}м²`;
}

const formatRateRange = (from: number, to: number | null, units: string): string => {
  // В монолите `to` сравнивался с несуществующей переменной и диапазон выводился всегда («X – X») — исправлено
  const range = to !== null && to !== from ? `${formatDecimal(from)} – ${formatDecimal(to)}` : formatDecimal(from);

  return noBreak(`${range} ₽ ${units}`);
};

/** Ставка: аренда за м² в год, без неё — продажа за м² */
export function getFloatingHeaderRate({ rentFrom, rentTo, saleFrom, saleTo }: IBuildingRates): string | null {
  if (rentFrom) {
    return formatRateRange(rentFrom, rentTo, 'за м² в год');
  }
  if (saleFrom) {
    return formatRateRange(saleFrom, saleTo, 'за м²');
  }

  return null;
}

/** Маркер под галереей: шапка появляется, когда он уходит под неё (`#cf-floating-header-breakpoint`) */
export const FLOATING_HEADER_BREAKPOINT_ID = 'floating-header-breakpoint';

const FLOATING_HEADER_SELECTOR = '[data-name="FloatingHeader"]';

/**
 * Высота плавающей шапки. Скрытая шапка сдвинута вверх через `visibility`, а не `display: none`,
 * поэтому высота измеряется и у скрытой (как `scrollHeight` в монолите)
 */
export const getFloatingHeaderHeight = (): number =>
  document.querySelector(FLOATING_HEADER_SELECTOR)?.scrollHeight ?? 0;

/** Положение элемента от начала документа */
export const getDocumentTop = (element: Element): number => element.getBoundingClientRect().top + window.scrollY;
